import type { Player } from '../poker/types';
import { cn } from '@/lib/utils';

interface PlayerAvatarProps {
  player: Player;
  showBet?: boolean;
  isActive?: boolean;
  size?: 'sm' | 'md';
}

export function PlayerAvatar({ player, showBet = false, isActive = false, size = 'md' }: PlayerAvatarProps) {
  const sizeClasses = {
    sm: 'text-2xl sm:text-3xl',
    md: 'text-3xl sm:text-4xl',
  };
  
  return (
    <div className={cn(
      'flex flex-col items-center gap-1 transition-all duration-300 relative pb-6',
      player.folded && 'opacity-30 grayscale',
    )}>
      {/* Avatar amb anell d'activitat */}
      <div className={cn(
        'relative p-1 rounded-full transition-all duration-300',
        isActive && 'bg-gradient-to-r from-amber-400 to-yellow-500 shadow-lg shadow-yellow-500/30'
      )}>
        <div className={cn(
          'rounded-full bg-zinc-800 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center transition-all',
          isActive && 'bg-zinc-900'
        )}>
          <span className={cn(sizeClasses[size], 'select-none block')}>
            {player.avatar}
          </span>
        </div>
        
        {/* Indicador de dealer */}
        {player.isDealer && (
          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-gradient-to-br from-yellow-300 to-amber-500 rounded-full flex items-center justify-center text-[10px] font-black text-zinc-900 shadow-lg border-2 border-zinc-950">
            D
          </div>
        )}
      </div>
      
      {/* Nom i fitxes */}
      <div className="flex flex-col items-center gap-0.5">
        <span className={cn(
          'text-[10px] sm:text-xs font-medium transition-colors',
          isActive ? 'text-white' : 'text-zinc-400',
          player.folded && 'line-through'
        )}>
          {player.name}
        </span>
        {!player.isHuman && player.folded && player.chips === 0 && (
          <span className="text-[9px] text-red-400 font-semibold mt-0.5">
            Sense fitxes
          </span>
        )}
        
        <span className={cn(
          'text-xs sm:text-sm font-bold tabular-nums',
          player.chips > 100 ? 'text-white' : player.chips > 50 ? 'text-yellow-400' : 'text-red-400'
        )}>
          {player.chips}
        </span>
      </div>
      
      {/* Última acció - posicionada absolutament a sota */}
      {player.lastAction && !player.folded && (
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
          <span className={cn(
            'text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded animate-fade-in whitespace-nowrap',
            player.lastAction === 'Puja' || player.lastAction === 'Aposta' 
              ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
              : player.lastAction === 'All-in'
              ? 'bg-red-500/30 text-red-300 border border-red-500/50'
              : player.lastAction === 'SB' || player.lastAction === 'BB'
              ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
              : 'bg-zinc-700 text-zinc-200 border border-zinc-600'
          )}>
            {player.lastAction}
          </span>
        </div>
      )}
    </div>
  );
}

interface PlayersRowProps {
  players: Player[];
  currentPlayerIndex: number;
  humanIndex?: number;
}

export function PlayersRow({ players, currentPlayerIndex, humanIndex }: PlayersRowProps) {
  // Mostrar tots els jugadors
  const allPlayers = players;
  
  return (
    <div className="flex justify-center items-end gap-2 sm:gap-4 md:gap-6 px-2">
      {allPlayers.map((player) => {
        const originalIndex = players.findIndex(p => p.id === player.id);
        return (
          <div key={player.id} className="relative">
            <PlayerAvatar 
              player={player} 
              isActive={originalIndex === currentPlayerIndex && !player.folded}
            />
            {/* Aposta */}
            {player.bet > 0 && (
              <div className="absolute -bottom-5 sm:-bottom-6 left-1/2 -translate-x-1/2 animate-fade-in">
                <span className="inline-flex items-center justify-center min-w-6 sm:min-w-7 h-5 sm:h-6 px-1.5 sm:px-2 bg-zinc-800 text-amber-400 text-[10px] sm:text-xs font-bold rounded-full border border-zinc-700">
                  {player.bet}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
